import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ExclusiveOffers = () => {
  const navigate = useNavigate();
  const API_BASE    = (import.meta).env.VITE_STOREFRONT_API_URL || 'https://dashboard.offloadhost.com';
  const CUSTOMER_ID = (import.meta).env.VITE_CUSTOMER_ID || '';

  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);

  // Flash sale data + featured product. Both come from the storefront API.
  // If no active flash sale, sale=null and the section returns null below.
  const [sale, setSale] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!CUSTOMER_ID) return;
    const ctl = new AbortController();
    Promise.all([
      fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/flash-sale`, { signal: ctl.signal }).then((r) => r.ok ? r.json() : null),
      fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/products`,   { signal: ctl.signal }).then((r) => r.ok ? r.json() : null),
    ]).then(async ([s, p]) => {
      if (!s || !s.title) { setSale(null); return; }
      setSale(s);
      const ids = Array.isArray(s.product_ids) ? s.product_ids : [];
      const products = p?.products || [];
      // flash_sale.product_ids are integer DB ids; the storefront feed
      // exposes only public_slug as `id`. Resolve int → slug via the
      // legacy-id resolver, then match by slug.
      let match = null;
      for (const intId of ids) {
        try {
          const rr = await fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/products/${intId}/resolve`, { signal: ctl.signal });
          if (!rr.ok) continue;
          const rd = await rr.json();
          if (!rd?.slug) continue;
          match = products.find((x) => String(x.id) === String(rd.slug));
          if (match) break;
        } catch (_) { /* keep trying */ }
      }
      if (match) setFeatured(match);
    }).catch((err) => { if (err?.name !== 'AbortError') console.error('flash-sale fetch failed:', err); });
    return () => ctl.abort();
  }, []);

  // Tick the countdown using the API ends_at as ground-truth.
  useEffect(() => {
    if (!sale?.ends_at) return;
    const endsMs = new Date(String(sale.ends_at).replace(' ', 'T') + 'Z').getTime();
    const tick = () => {
      const diff = Math.max(0, endsMs - Date.now());
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const sec = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ hours: h, minutes: m, seconds: sec });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sale?.ends_at]);

  // No active flash sale → don't render this section at all.
  if (!sale) return null;

  const handleShopOffer = () => {
    if (featured?.id) navigate(`/product-details?id=${featured.id}`); else navigate('/product-catalog');
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Parallax Background */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="bg-card/80 backdrop-blur-sm border border-border rounded-3xl overflow-hidden shadow-2xl">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Content Side */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}>

                <span className="inline-block bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full mb-4">
                  {sale.discount_label || 'Limited Time Offer'}
                </span>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
                  {sale.title}
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  {sale.subtitle}
                </p>

                {/* Countdown Timer */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="text-center">
                    <div className="bg-primary/10 rounded-lg px-4 py-3 mb-2">
                      <span className="text-3xl font-bold text-primary">
                        {String(timeLeft?.hours)?.padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground uppercase">Hours</span>
                  </div>
                  <span className="text-2xl font-bold text-muted-foreground">:</span>
                  <div className="text-center">
                    <div className="bg-primary/10 rounded-lg px-4 py-3 mb-2">
                      <span className="text-3xl font-bold text-primary">
                        {String(timeLeft?.minutes)?.padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground uppercase">Minutes</span>
                  </div>
                  <span className="text-2xl font-bold text-muted-foreground">:</span>
                  <div className="text-center">
                    <div className="bg-primary/10 rounded-lg px-4 py-3 mb-2">
                      <span className="text-3xl font-bold text-primary">
                        {String(timeLeft?.seconds)?.padStart(2, '0')}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground uppercase">Seconds</span>
                  </div>
                </div>

                <Button
                  onClick={handleShopOffer}
                  size="lg"
                  className="w-full sm:w-auto group">

                  Shop Flash Sale
                  <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>
            </div>

            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-64 lg:h-auto">

              <img
                src={sale.background_image_url || featured?.image_url || ''}
                alt={featured?.name || sale.title}
                className="absolute inset-0 w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>);

};

export default ExclusiveOffers;